import { useEffect, useState } from 'react'

import { useResourcesApi } from '@/services/api/resourcesApi'
import { SapOptions } from '@/types'
import { MessageLevel, ErrorHandlingStrategy } from '@/types/api'

interface DocumentType {
    value: string
    label: string
}

interface UseDocumentTypesReturn {
    documentTypes: DocumentType[]
    isLoading: boolean
    hasError: boolean
}

export const useDocumentTypes = (): UseDocumentTypesReturn => {
    const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [hasError, setHasError] = useState(false)

    const resourcesApi = useResourcesApi({
        messageLevel: MessageLevel.ERRORS_AND_INFO_ONLY,
        errorHandling: ErrorHandlingStrategy.RETURN
    })

    useEffect(() => {
        let mounted = true

        const loadDocumentTypes = async () => {
            try {
                setIsLoading(true)
                setHasError(false)

                const response = await resourcesApi.getDocumentTypes()

                if (!mounted) {
                    return
                }

                console.log('📡 useDocumentTypes: API response:', response)

                // Verificar si la respuesta es un array directo o un objeto con status/data
                let data: SapOptions[] = []

                if (Array.isArray(response)) {
                    // La respuesta es directamente un array
                    data = response
                    console.log(
                        '✅ useDocumentTypes: Direct array response:',
                        data
                    )
                } else if (
                    response &&
                    response.data &&
                    Array.isArray(response.data)
                ) {
                    // La respuesta es un objeto con status y data
                    data = response.data
                    console.log(
                        '✅ useDocumentTypes: Object response with data:',
                        data
                    )
                } else {
                    console.log(
                        '❌ useDocumentTypes: Invalid response format:',
                        response
                    )
                    setHasError(true)

                    return
                }

                if (data && data.length > 0) {
                    const formattedData = data
                        .filter((item: SapOptions) => {
                            const hasValue =
                                item && item.value && item.value.trim() !== ''
                            const hasLabel =
                                item && item.label && item.label.trim() !== ''

                            return hasValue && hasLabel
                        })
                        .map((item: SapOptions) => ({
                            value: item.value,
                            label: item.label
                        }))

                    console.log(
                        '🎯 useDocumentTypes: Formatted data:',
                        formattedData
                    )
                    setDocumentTypes(formattedData)
                    setHasError(false)
                } else {
                    console.log('❌ useDocumentTypes: No valid data found')
                    setHasError(true)
                }
            } catch (error) {
                console.error('Error loading document types:', error)
                if (mounted) {
                    setHasError(true)
                }
            } finally {
                if (mounted) {
                    setIsLoading(false)
                }
            }
        }

        loadDocumentTypes()

        return () => {
            mounted = false
        }
    }, [])

    return {
        documentTypes,
        isLoading,
        hasError
    }
}
